import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CategoryContentResponseModel,
  ShopSubCategoriesListApiElementContentModel,
  SubCategoryContentResponseModel,
} from '@/services/platform/api/model';
import { cn, ModuleProperties, useClientTranslation, useLocaleStore } from '@/lib/core';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import ModuleWrapper from '@/components/shared/module/wrapper';
import { getCategoryById, getSubCategoriesByCategoryId } from '@/services/platform/data/shop/catalog';

export const ShopSubCategoriesListApi = (props: ModuleProperties) => {
  const content = props.content as ShopSubCategoriesListApiElementContentModel | undefined;
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const setScreenHeadOverride = useUIStore(state => state.setScreenHeadOverride);
  const location = useLocation();
  const navigate = useNavigate();

  const lastSegment = location.pathname.split('/').pop() ?? '';
  const categoryId = lastSegment.includes('_') ? lastSegment.split('_').slice(1).join('_') : '';

  const [category, setCategory] = useState<CategoryContentResponseModel | undefined>(undefined);
  const [subcategories, setSubcategories] = useState<SubCategoryContentResponseModel[]>([]);

  useEffect(() => {
    if (!categoryId) return;
    getCategoryById(categoryId, locale).then(result => {
      setCategory(result);
      if (result?.name) {
        const segments = result.route.path.split('/').filter(Boolean);
        const section = segments[segments.length - 2];
        setScreenHeadOverride({ title: result.name, titleSuffix: section });
      }
    });
    return () => setScreenHeadOverride(null);
  }, [categoryId, locale, setScreenHeadOverride]);

  useEffect(() => {
    if (!categoryId) return;
    getSubCategoriesByCategoryId(categoryId, locale).then(result => {
      if (result) setSubcategories(result);
    });
  }, [categoryId, locale]);

  if (!content) return null;

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        base: cn(['module__shop-subcategories-list']),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex flex-col flex-1 gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      <div className="flex flex-col px-4">
        {category && (
          <button
            type="button"
            className="border-jncquoi-neutral-200 flex h-14.25 w-full items-center border-b text-left"
            onClick={() => {
              if (category.route?.path) navigate(`${location.pathname}/subcategory_all`);
            }}
          >
            <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] leading-none font-normal tracking-[0.5px] uppercase">
              {t('App.Labels.All')} {category.name}
            </span>
          </button>
        )}
        {subcategories.map(item => (
          <button
            key={item.id}
            type="button"
            className="border-jncquoi-neutral-200 flex h-14.25 w-full items-center border-b text-left"
            onClick={() => navigate(`${location.pathname}/subcategory_${item.id}`)}
          >
            <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] leading-none font-normal tracking-[0.5px] uppercase">
              {item.name}
            </span>
          </button>
        ))}
      </div>
    </ModuleWrapper>
  );
};
